import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { formatJordan } from '@serb/shared';
import CloseIcon from '@mui/icons-material/Close';
import { FormField } from '../../components/FormField';
import { useNotice } from '../../components/NoticeProvider';
import { clockApi } from '../clock/useClock';
import { useShipments } from '../shipments/useShipments';
import { useClaudeTasks } from '../claudeTasks/useClaudeTasks';
import { useAction } from '../../lib/useAction';

const EXAMPLES = [
  'Check latest rates for Turkey → Aqaba',
  'Prepare a quotation for SRB-625',
  'Follow up with agent for missing invoice',
  'Create a draft email to customer ABC Trading',
  'Update SRB-640 with new tracking information',
];

const blank = {
  srb: '',
  details: '',
  when: 'now' as 'now' | 'later',
  date: '',
  time: '10:00',
  reminder: true,
  rule: false,
};

/** A panel for asking Claude to do something that is not one of the approvals. Scheduling it can also put it in Reminders. */
export function GiveTaskDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const notice = useNotice();
  const [form, setForm] = useState(blank);
  const set = (patch: Partial<typeof blank>) => setForm((f) => ({ ...f, ...patch }));
  const remind = useAction(clockApi.add, {
    success: (a) => `Also added to Reminders & Calendar for ${formatJordan(a.deliverAt)}.`,
  });
  const close = () => {
    setForm(blank);
    onClose();
  };
  const tasks = useClaudeTasks();
  const give = useAction(tasks.add, {
    success: (t) =>
      `${t.runAt ? `Claude will do this at ${formatJordan(t.runAt)}` : 'Claude starts on this within a minute'}.${t.rule ? ' It is also kept as a rule for Claude.' : ''}`,
    onDone: () => {
      if (form.when === 'later' && form.reminder)
        remind.run({
          title: `Task for Claude: ${form.details.trim().slice(0, 80)}`,
          code: form.srb,
          at: `${form.date}T${form.time}`,
          urgent: false,
        });
      close();
    },
  });
  const { items: shipments } = useShipments();
  const submit = () => {
    if (!form.details.trim()) return notice('Write what you want Claude to do first.', 'warning');
    if (form.when === 'later' && !form.date) return notice('Choose the date for later.', 'warning');
    give.run({
      task: form.details.trim(),
      code: form.srb,
      at: form.when === 'later' ? `${form.date}T${form.time}` : '',
      rule: form.rule,
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={close}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 } } } }}
    >
      <Stack spacing={2} sx={{ p: { xs: 1.5, sm: 2.5 }, pt: 'max(16px, env(safe-area-inset-top))' }}>
        <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 750 }}>
              Give Claude a Task
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tell Claude to do something outside of the current approvals. This will be added to Claude&apos;s task
              list.
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={close}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <FormField
          label="Shipment Number (optional)"
          value={form.srb}
          onChange={(srb) => set({ srb })}
          options={shipments.map((s) => s.srb)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          Leave empty if this is not related to a specific shipment.
        </Typography>

        <TextField
          label="Task Details"
          required
          multiline
          minRows={5}
          placeholder="Tell Claude what you want it to do…"
          value={form.details}
          onChange={(e) => set({ details: e.target.value })}
        />
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary">
            Examples:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 13 }}>
            {EXAMPLES.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </Box>
        </Box>

        <Typography sx={{ fontWeight: 700 }}>When Should Claude Do This?</Typography>
        <RadioGroup value={form.when} onChange={(e) => set({ when: e.target.value as 'now' | 'later' })}>
          <FormControlLabel
            value="now"
            control={<Radio size="small" />}
            label={<Choice title="Now" help="Start the task immediately." />}
          />
          <FormControlLabel
            value="later"
            control={<Radio size="small" />}
            label={<Choice title="Schedule for later" help="Choose a date and time." />}
          />
          <FormControlLabel
            value="repeat"
            disabled
            control={<Radio size="small" />}
            label={<Choice title="Repeat regularly" help="Coming soon: set a recurring schedule." />}
          />
        </RadioGroup>
        {form.when === 'later' && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Date"
                type="date"
                value={form.date}
                onChange={(e) => set({ date: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label="Time"
                type="time"
                value={form.time}
                onChange={(e) => set({ time: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Jordan time (Asia/Amman)
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={form.reminder} onChange={(e) => set({ reminder: e.target.checked })} />}
              label="Add to Reminders & Calendar"
            />
          </Stack>
        )}

        <Typography sx={{ fontWeight: 700 }}>Should Claude Remember This Instruction?</Typography>
        <RadioGroup value={form.rule ? 'rule' : 'once'} onChange={(e) => set({ rule: e.target.value === 'rule' })}>
          <FormControlLabel
            value="once"
            control={<Radio size="small" />}
            label={<Choice title="This time only" help="Apply this task to your current request only." />}
          />
          <FormControlLabel
            value="rule"
            control={<Radio size="small" />}
            label={
              <Choice
                title="Save as a rule"
                help="Save this instruction to SERB Mind → Learned Lessons for future use."
              />
            }
          />
        </RadioGroup>

        <Stack direction="row" spacing={1}>
          <Button fullWidth variant="outlined" onClick={close}>
            Cancel
          </Button>
          <Button fullWidth variant="contained" disabled={give.busy || remind.busy} onClick={submit}>
            Give Task to Claude
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}

const Choice = ({ title, help }: { title: string; help: string }) => (
  <Box>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {help}
    </Typography>
  </Box>
);
